<template>
    <section class="settings-wrapper">
        <div class="settings-page">
            <div class="settings-page__inner">
                <aside-navigation
                    :routes="routes"
                    :title="title"
                    :default-route="defaultRoute"
                    class="settings-page__navigation"
                />
                <div class="settings-page__child-wrap">
                    <nuxt-page
                        ref="content"
                        @back-to-navigation="backToNavigation"
                        @data-loading="onChildDataLoaded"
                    />
                    <div v-if="showLoader" class="settings-page__loader">
                        <loader />
                    </div>
                </div>
            </div>
            <div class="settings-page__version">{{ captionAppVersion }}</div>
            <div class="settings-page__tip">{{ tip }}</div>
        </div>
    </section>
</template>

<script lang="ts" setup>
    import { ref, type Ref, computed, nextTick, onMounted, onBeforeUnmount, onBeforeMount, watch  } from "vue";
    import { useRouter, useRoute } from 'vue-router';
    import { linkItems as menuItems } from "@/components/Header/types/LinkItemsCollection";
    import { Strings } from "@/locales/types/enums/strings";
    import { Logger } from '@/utils';
    import type { LinkItem } from "@/components/Header/types/LinkItem";
    import { useDevModeState, type IUseDevModeState } from "@/composables/devMode/useDevModeState"
    import { useI18n } from "vue-i18n";
    import { useLanguagesUtils } from "@/composables/useLanguagesUtils";
    import { AvailabilityLanguages } from "@/common/types/languages"
    import { useUserInfoStore } from "@/stores/UserInfo";
    import { useDeviceSettingsStore } from "@/stores/DeviceSettings";
    import { storeToRefs } from 'pinia';
    import { ScreenName } from "@/common/types/screen-names";


    const UserInfoStoreModule = useUserInfoStore();
    const {
        isClient,
    } = storeToRefs(UserInfoStoreModule);

    const { linkItems, appVersion, buildCounter } = useAppConfig();
    const { $fire, $i18n, $eventBus, $analytics } = useNuxtApp();

    const traceSettingsScreen = $fire.trace($fire.performance, 'SETTINGS_SCREEN');
    try {
        traceSettingsScreen.start();
        Logger.captureLog('SETTINGS_SCREEN start');
    } catch (e){
        Logger.captureLog(e);
    }

    const { isEnabledDevMode }: IUseDevModeState = useDevModeState()
    const { t } = useI18n();
    const router = useRouter();
    const route = useRoute();
    const { setupSettings } = useDeviceSettingsStore();
    const { availabilityLanguages } = useLanguagesUtils();

    const showLoader: Ref<boolean> = ref(false);
    const routes: Ref<LinkItem[]> = ref([]);

    const captionAppVersion: string = $i18n.t(Strings.SETTINGS_VERSION, [`${appVersion}.${buildCounter}`]).toString();
    const title = computed((): string => t(Strings.SETTINGS_TITLE).toString());
    const defaultRoute: string = menuItems.settings.routerPath;
    const tip: string = ""; // @todo Lately must obtain tips text from children via events.

    const onChildDataLoaded = (event: boolean): void => {
        showLoader.value = event;
    };

    const backToNavigation = (): void => {
        void router.push(menuItems.settings.routerPath);
        showLoader.value = false;
    };

    const initDevMode = (value: boolean): void => {
        if (value) {
            if (routes.value.some(({ code }) => code === menuItems.devMode.code)) {
                return;
            };
            routes.value = [
                ...routes.value,
                menuItems.devMode,
            ];
        } else {
            routes.value = routes.value.filter(({ code }) => code !== menuItems.devMode.code);
        }
    };

    const initRoutes = (): void => {

        const res: LinkItem[] = [];
        
        let menu = isClient.value ? linkItems.settings.client : linkItems.settings.guest;
        const availabilityLangs: AvailabilityLanguages = availabilityLanguages();
        if (availabilityLangs !== AvailabilityLanguages.ALL) {
            menu = menu.filter((item) => item !== 'language');
        }
        if (menu.length > 0) {
            menu.forEach(item => {
                res.push(menuItems[item]);
            });
        }

        routes.value = res;
    };

    const autoRedirectToSubpage = (): void => {
        try {
            const linkItemCode = route.query.linkItemCode;
            if (linkItemCode === undefined || linkItemCode === null || linkItemCode === "" || isNaN(Number(linkItemCode))) {
                return;
            }

            const findRoute: LinkItem | undefined = routes.value.find((item: LinkItem) => item.code === Number(linkItemCode));
            if (findRoute) {
                const linkElement: HTMLAnchorElement | null = document.querySelector(`a[href='#${findRoute.routerPath}']`);
                linkElement?.click();
            }
        } catch (error) {
            Logger.captureError(error as Error);
        }
    };

    onBeforeMount(async () => {
        await setupSettings();
        initRoutes();
    })

    onMounted(() => {
        void nextTick((): void => {
            autoRedirectToSubpage();

            $eventBus.on("data-loading", onChildDataLoaded);
            $analytics.createScreenEvent(ScreenName.SETTINGS_PAGE, ScreenName.SETTINGS_PAGE);

            if (traceSettingsScreen) {
                try {
                    traceSettingsScreen.stop();
                    Logger.captureLog(`SETTINGS_SCREEN stop ${traceSettingsScreen.durationUs / 1000000} sec`);
                } catch (e){
                    Logger.captureLog(e);
                }
            }
        });
        initDevMode(isEnabledDevMode.value)
    });

    onBeforeUnmount(() => {
        $eventBus.off("data-loading", onChildDataLoaded);
    });

    watch(
        isEnabledDevMode, 
        (value: boolean): void => {
            initDevMode(value);
        }
    );
</script>

<style lang="scss">
    @import url("settings/styles.scss");
</style>